/** @jsxImportSource @emotion/react */

import Typography from '@mui/material/Typography'
import { Dispatch, FC, SetStateAction, SyntheticEvent } from 'react'
import Dropzone from 'react-dropzone'
import { generate as generateUniqueId } from 'randomstring'
import readFileAsDataURL from 'utils/readFileAsDataURL'
import { ReactComponent as TrashIcon } from 'assets/icons/cross.svg'
import { MAX_AVAILABLE_FILES_TO_UPLOAD_AS_IMAGE } from 'constants/images'
import * as classes from './styles'

interface IProps {
  isEditMode: boolean
  imageError: boolean
  selectedImage: File | null | undefined
  setSelectedImage: Dispatch<SetStateAction<File | null | undefined>>
  setImageError: Dispatch<SetStateAction<boolean>>
  id?: string
}

const AvatarInput: FC<IProps> = ({
  isEditMode,
  setSelectedImage,
  selectedImage,
  setImageError,
  id = generateUniqueId(),
  imageError,
}) => {
  const handleFileSelect = (selectedFile: File): void => {
    if (isEditMode) {
      readFileAsDataURL(selectedFile).then((res) => {
        const img = new Image()
        img.onload = () => {
          if (img.width === img.height) {
            setSelectedImage(selectedFile)
            setImageError(false)
          } else {
            setImageError(true)
            setSelectedImage(null)
          }
        }
        img.src = res as string
      })
    }
  }

  const removeImage = (e: SyntheticEvent<SVGSVGElement>) => {
    e.preventDefault()
    e.stopPropagation()
    isEditMode && setSelectedImage(null)
  }

  return (
    <Dropzone
      disabled={!isEditMode}
      maxFiles={MAX_AVAILABLE_FILES_TO_UPLOAD_AS_IMAGE}
      onDrop={(acceptedFiles) => handleFileSelect(acceptedFiles[0])}
    >
      {({ getRootProps, getInputProps }) => (
        <div {...getRootProps()} css={isEditMode && classes.imageInput}>
          <div css={[classes.avatarField, imageError && classes.avatarFieldError]}>
            <label htmlFor={id}>
              <input {...getInputProps()} />
              {selectedImage ? (
                <div css={[classes.avatarContainer, isEditMode && classes.imageInput]}>
                  <div css={classes.selectedImage}>
                    <img css={classes.attachment} src={URL.createObjectURL(selectedImage)} alt="Reviewer avatar" />
                    <Typography variant="body2" css={classes.imageTitle}>
                      {selectedImage.name}
                    </Typography>
                  </div>
                  <TrashIcon
                    css={[classes.removeIcon, isEditMode && classes.imageInput]}
                    onClick={(e) => removeImage(e)}
                    data-testid="remove-image-button"
                  />
                </div>
              ) : (
                <div css={classes.dndImageArea}>
                  <TrashIcon css={classes.addIcon} />
                  <Typography
                    variant="body2"
                    css={[classes.dndImageAreaTitle, imageError && classes.dndImageAreaHintError]}
                  >
                    Drop your content
                  </Typography>
                  <Typography
                    variant="body3"
                    css={[classes.dndImageAreaHint, imageError && classes.dndImageAreaHintError]}
                  >
                    Image should be square
                  </Typography>
                </div>
              )}
            </label>
          </div>
        </div>
      )}
    </Dropzone>
  )
}

export default AvatarInput
